import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Button, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { GradientText } from '../ui/GradientText';
import { SurveyMockup } from '../ui/SurveyMockup';
import { HERO_CONTENT } from '../../constants';
import logo from '../../../../../assets/logo.png';

/**
 * Hero section for the landing page homepage
 * Full-height section with animated survey mockups and CTA buttons
 */
export const HeroSection: React.FC = () => {
  const theme = useTheme();

  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      component="section"
      sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
        py: { xs: 8, md: 0 },
      }}
    >
      {/* Animated Survey Mockups - Hidden on small screens via CSS */}
      <SurveyMockup instance={1} />
      <SurveyMockup instance={2} />
      <SurveyMockup instance={3} />
      <SurveyMockup instance={4} />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center' }}>
          {/* Logo - data-hero-logo used by useHeroVisibility hook */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            data-hero-logo
          >
            <Box
              component="img"
              src={logo}
              alt="Lecturo Logo"
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                mb: 3,
              }}
            />
          </motion.div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          >
            <GradientText
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 1,
              }}
            >
              {HERO_CONTENT.title}
            </GradientText>
          </motion.div>

          {/* Subtitle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
          >
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{
                fontWeight: 400,
                mb: 1.5,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
              {HERO_CONTENT.subtitle}
            </Typography>
          </motion.div>

          {/* Gradient Line */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }}
          >
            <Box
              sx={{
                width: 60,
                height: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: 2,
                mx: 'auto',
                mb: 3,
              }}
            />
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5, ease: 'easeOut' }}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 4,
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.8,
                fontSize: { xs: '1rem', md: '1.1rem' },
              }}
            >
              {HERO_CONTENT.description}
            </Typography>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6, ease: 'easeOut' }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                component={RouterLink}
                to="/demo"
                variant="contained"
                size="large"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderRadius: 2,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[8],
                  },
                  transition: theme.transitions.create(['transform', 'box-shadow'], {
                    duration: theme.transitions.duration.short,
                  }),
                }}
              >
                {HERO_CONTENT.primaryCta}
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={scrollToFeatures}
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderRadius: 2,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: theme.palette.primary.dark,
                    bgcolor: theme.palette.action.hover,
                    transform: 'translateY(-2px)',
                  },
                  transition: theme.transitions.create(['transform', 'background-color'], {
                    duration: theme.transitions.duration.short,
                  }),
                }}
              >
                {HERO_CONTENT.secondaryCta}
              </Button>
            </Box>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};
